import { useMemo } from 'react'
import { AssetsModule } from '@/core/modules/digital-footprint/assets'
import { TechnologiesModule } from '@/core/modules/digital-footprint/technologies/technologies.module'
import { sortAssociatedTechnologies } from '@/core/modules/digital-footprint/technologies/technologies.filters'
import { useDrawer } from '@core/context/DrawerContext'
import { useAllIssues } from '@/ui/routes/issues/hooks/useAllIssues'
import { TAssociatedTechnology } from '@ui/drawer/common/AssociatedTechnologiesTable'

export const useAssetDetails = () => {
    const { drawerState } = useDrawer()
    const moduleAssets = AssetsModule.getData()
    const { index, assets: sortedAssets } = drawerState?.contentProps || {}
    const filteredAssets = sortedAssets ?? moduleAssets
    const currentAsset = filteredAssets[index]
    const { data: allIssues } = useAllIssues()
    const { data: allTechnologies } = TechnologiesModule.useStore()

    const {
        asset_type: assetType,
        issues: drawerIssues,
        technologies: assetTechnologies,
    } = currentAsset || {}

    const enrichedAssetIssues: TIssue[] = useMemo(() => {
        if (!drawerIssues || !allIssues) return []

        const openAssetIssues = drawerIssues.filter(
            (assetIssue: TIssue) => assetIssue.status === 'open'
        )

        const enrichedIssues = openAssetIssues.flatMap((assetIssue: TIssue) =>
            allIssues.filter(
                (issue: TIssue) =>
                    issue.id === assetIssue.id ||
                    issue.id === assetIssue?.finding_id
            )
        )

        return enrichedIssues.sort(
            (a: TIssue, b: TIssue) =>
                (b?.severity_score || 0) - (a?.severity_score || 0)
        )
    }, [allIssues, drawerIssues])

    const fullTechnologies: TAssociatedTechnology[] = useMemo(() => {
        if (!assetTechnologies?.length) return []

        const techs = assetTechnologies as Partial<TTechnology>[]
        const entries = techs.map<TAssociatedTechnology>((tech) => {
            const fullTech = TechnologiesModule.findTechnologyByName(tech.name)
            return {
                ...(tech as TTechnology),
                ...(fullTech ?? {}),
                isClickable: Boolean(fullTech),
            }
        })
        return sortAssociatedTechnologies(entries)
    }, [allTechnologies, assetTechnologies])

    return {
        currentAsset,
        assetType,
        enrichedAssetIssues,
        fullTechnologies,
    }
}
